import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PageHeader } from '../components/Navigation';
import { getBackPath } from '../utils/navigation';
import '../InvoiceDetail.css';
import '../Clients.css';
import { apiFetch } from '../utils/api';
import { useClientDetail, useClientEndUsersList } from '../hooks/useClients';

// A simple, reusable message component for displaying feedback
const FormMessage = ({ message }) => {
  if (!message) return null;
  const messageClass = message.type === 'error'
    ? 'validation-message validation-message-error'
    : 'validation-message validation-message-success';

  return (
    <div className={messageClass}>
      <p className="validation-message-text">{message.text}</p>
    </div>
  );
};

export default function ProductCreatePage() {
  const { id: clientIdFromUrl, productId } = useParams();  // productId is present in edit mode
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!productId;  // If productId exists, we're in edit mode

  const [clientId, setClientId] = useState(clientIdFromUrl);  // Will be set from product data if missing

  const { data: clientData, status: clientStatus, error: clientError } = useClientDetail(clientId);
  const { data: endUsers, status: endUsersStatus } = useClientEndUsersList(clientId);
  const client = clientData?.client;
  const backPath = getBackPath(location, clientId ? `/clients/${clientId}` : '/clients');

  const breadcrumbs = [
    { label: 'Validare', to: '/validation-hub' },
    { label: 'Emitenți', to: '/clients' },
    { label: client?.nume || 'Emitent', to: clientId ? `/clients/${clientId}` : '/clients' },
    { label: isEditMode ? 'Editare Articol' : 'Creare Articol', to: null }
  ];

  const [form, setForm] = useState({
    nume: '',
    pret_unitar_net: '',
    moneda: 'RON',
    cantitate_implicita: '1',
  });
  const [selectedEndUserIds, setSelectedEndUserIds] = useState([]);
  const [hasEmailRestriction, setHasEmailRestriction] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);

  // Load product data if in edit mode
  useEffect(() => {
    if (isEditMode && productId) {
      setLoadingProduct(true);
      apiFetch(`/api/products/${productId}`)
        .then(async (res) => {
          if (res.ok) {
            const data = await res.json();
            console.log('Loaded product for editing:', data);

            // Set clientId from product data if not in URL
            if (!clientIdFromUrl && data.product.id_client) {
              setClientId(data.product.id_client);
            }

            // Pre-fill form with existing product data
            setForm({
              nume: data.product.nume || '',
              pret_unitar_net: String(data.product.pret_unitar_net || ''),
              moneda: data.product.moneda || 'RON',
              cantitate_implicita: String(data.product.cantitate_implicita || '1'),
            });

            // Pre-select linked end users
            if (data.product.linked_end_user_ids) {
              setSelectedEndUserIds(data.product.linked_end_user_ids);
            }

            // Pre-fill email restriction data
            if (data.product.has_email_restriction) {
              setHasEmailRestriction(true);
              if (data.product.restricted_to_emails && Array.isArray(data.product.restricted_to_emails)) {
                setSelectedEmails(data.product.restricted_to_emails);
              }
            }
          } else {
            setMessage({ type: 'error', text: 'Eroare la încărcarea articolului' });
          }
        })
        .catch((err) => {
          console.error('Error loading product:', err);
          setMessage({ type: 'error', text: 'Eroare la încărcarea articolului' });
        })
        .finally(() => {
          setLoadingProduct(false);
        });
    }
  }, [isEditMode, productId, clientIdFromUrl]);

  const errors = useMemo(() => {
    const e = {};
    if (!form.nume.trim()) e.nume = 'Denumirea este obligatorie';
    const price = parseFloat(form.pret_unitar_net);
    if (!(price > 0)) e.pret_unitar_net = 'Prețul trebuie să fie > 0';
    if (!form.moneda.trim()) e.moneda = 'Moneda este obligatorie';
    const quantity = parseFloat(form.cantitate_implicita);
    if (!(quantity > 0)) e.cantitate_implicita = 'Cantitatea trebuie să fie > 0';
    return e;
  }, [form]);

  const allValid = Object.keys(errors).length === 0;

  async function handleSave() {
    if (!allValid) return;
    setSaving(true);
    setMessage(null);
    try {
      // Use selectedEmails array directly
      const emailArray = hasEmailRestriction && selectedEmails.length > 0
        ? selectedEmails
        : null;

      const payload = {
        product_data: {
          nume: form.nume,
          pret_unitar_net: parseFloat(form.pret_unitar_net),
          moneda: form.moneda.trim().toUpperCase(),
          cantitate_implicita: parseFloat(form.cantitate_implicita),
          has_email_restriction: hasEmailRestriction,
          restricted_to_emails: emailArray,
        },
        end_user_ids: selectedEndUserIds,
      };

      let res;
      if (isEditMode) {
        // UPDATE existing product
        res = await apiFetch(`/api/products/${productId}/update-links`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        // CREATE new product
        res = await apiFetch(`/api/clients/${clientId}/products`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        let msg = isEditMode ? 'Eroare la actualizarea articolului' : 'Eroare la crearea articolului';
        try { const p = await res.clone().json(); if (p?.msg) msg = p.msg; } catch (_) {}
        throw new Error(msg);
      }

      const successMessage = {
        type: 'success',
        text: isEditMode
          ? `Articolul "${form.nume}" a fost actualizat cu succes.`
          : `Articolul "${form.nume}" a fost creat și asociat cu succes.`
      };

      navigate(`/clients/${clientId}`, { state: { message: successMessage } });

    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  }

  function handleEndUserSelect(userId, isChecked) {
    if (isChecked) {
      setSelectedEndUserIds(prev => [...prev, userId]);
    } else {
      setSelectedEndUserIds(prev => prev.filter(id => id !== userId));
    }
  }

  function handleEmailSelect(email, isChecked) {
    if (isChecked) {
      setSelectedEmails(prev => [...prev, email]);
    } else {
      setSelectedEmails(prev => prev.filter(e => e !== email));
    }
  }

  if (loadingProduct) {
    return (
      <div className="invoice-detail">
        <main className="invoice-detail-main">Se încarcă articolul...</main>
      </div>
    );
  }

  return (
    <div className="invoice-detail">
      <PageHeader
        backTo={backPath}
        backLabel="Înapoi la Emitent"
        title={isEditMode ? 'Editare Articol & Relații' : 'Creare Articol & Relații'}
        subtitle={isEditMode
          ? 'Modificați articolul și gestionați asocierile cu beneficiarii'
          : 'Creați un articol nou și asociați-l cu beneficiarii'}
        breadcrumbs={breadcrumbs}
      />

      <main className="invoice-detail-main">
        <div className="invoice-detail-content">
          <FormMessage message={message} />

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Emitent</h2>
              <p className="section-subtitle">
                {isEditMode ? 'Articolul aparține acestui emitent' : 'Articolul va fi creat pentru acest emitent'}
              </p>
            </div>
            <div className="section-content">
              {clientStatus === 'loading' && 'Se încarcă...'}
              {clientStatus === 'error' && <div style={{ color: '#b91c1c' }}>{clientError}</div>}
              {clientStatus === 'success' && client && (
                <div className="form-grid form-grid-2">
                  <Field label="Denumire" value={client.nume || '—'} />
                  <Field label="CUI" value={client.cui || '—'} mono />
                  <Field label="Email" value={client.email || '—'} />
                  <Field label="Nr. Reg. Com." value={client.nr_reg_com || '—'} mono />
                  <Field label="Țară" value={client.tara_cod || '—'} />
                  <Field label="Județ" value={client.judet || '—'} />
                  <Field label="Oraș" value={client.oras || '—'} />
                  <Field label="Adresă" value={client.adresa || '—'} full />
                </div>
              )}
            </div>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Date Articol</h2>
              <p className="section-subtitle">TVA-ul va fi preluat automat din profilul emitentului.</p>
            </div>
            <div className="section-content">
              <div className="form-grid form-grid-2">
                <Input label="Denumire" value={form.nume} onChange={(v) => setForm((f) => ({ ...f, nume: v }))} error={errors.nume} full />
                <Input label="Preț Unitar Net" value={form.pret_unitar_net} onChange={(v) => setForm((f) => ({ ...f, pret_unitar_net: v }))} error={errors.pret_unitar_net} mono />
                <Input label="Monedă" value={form.moneda} onChange={(v) => setForm((f) => ({ ...f, moneda: v }))} error={errors.moneda} placeholder="ex: RON, EUR, USD" />
                <Input label="Cantitate Implicită" value={form.cantitate_implicita} onChange={(v) => setForm((f) => ({ ...f, cantitate_implicita: v }))} error={errors.cantitate_implicita} mono />
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Restricții Email</h2>
              <p className="section-subtitle">
                Limitați acest produs la anumite adrese de email ale emitentului
              </p>
            </div>
            <div className="section-content">
              <div className="form-grid">
                <div className="form-field party-address">
                  <label className="checkbox-item" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={hasEmailRestriction}
                      onChange={(e) => setHasEmailRestriction(e.target.checked)}
                    />
                    <span style={{ fontWeight: '500' }}>Restricționează acest produs la anumite email-uri</span>
                  </label>

                  {hasEmailRestriction && (
                    <div style={{ marginTop: '1rem' }}>
                      <div className="form-field-label" style={{ marginBottom: '0.75rem' }}>
                        Selectați adresele de email permise pentru acest produs:
                      </div>
                      {clientStatus === 'loading' && <div style={{ padding: '1rem', color: '#746c5a' }}>Se încarcă adresele de email...</div>}
                      {clientStatus === 'error' && <div style={{ padding: '1rem', color: '#b91c1c' }}>Eroare la încărcarea adreselor de email.</div>}
                      {clientStatus === 'success' && client && (
                        <>
                          {(!client.email || client.email.length === 0) ? (
                            <div style={{ padding: '1rem', color: '#746c5a', backgroundColor: '#fef3c7', borderRadius: '0.5rem' }}>
                              ⚠️ Acest emitent nu are adrese de email configurate. Adăugați adrese de email în profilul emitentului pentru a activa restricțiile.
                            </div>
                          ) : (
                            <>
                              <div className="checkbox-list">
                                {client.email.map((email, idx) => (
                                  <label key={idx} className="checkbox-item">
                                    <input
                                      type="checkbox"
                                      checked={selectedEmails.includes(email)}
                                      onChange={(e) => handleEmailSelect(email, e.target.checked)}
                                    />
                                    <span style={{ fontFamily: 'monospace' }}>{email}</span>
                                  </label>
                                ))}
                              </div>
                              <div style={{
                                marginTop: '0.75rem',
                                fontSize: '0.875rem',
                                color: '#746c5a',
                                fontStyle: 'italic'
                              }}>
                                ℹ️ Acest produs va apărea în catalog DOAR când emailul vine de la una din adresele selectate.
                                Produsele fără restricții apar pentru toate emailurile.
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {!hasEmailRestriction && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '1rem',
                      backgroundColor: '#eae7de',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#5c5443'
                    }}>
                      ✓ Acest produs va fi disponibil pentru TOATE adresele de email ale emitentului.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Asociază Beneficiari</h2>
              <p className="section-subtitle">
                Bifați beneficiarii care folosesc frecvent acest produs.
                {isEditMode && <strong> Doar beneficiarii valizi sunt afișați.</strong>}
              </p>
            </div>
            <div className="section-content">
              {endUsersStatus === 'loading' && 'Se încarcă beneficiarii...'}
              {endUsersStatus === 'error' && <div style={{ color: '#b91c1c' }}>Eroare la încărcarea beneficiarilor.</div>}
              {endUsersStatus === 'success' && (
                <>
                  {endUsers.length === 0 ? (
                    <div style={{ padding: '1rem', color: '#746c5a' }}>
                      Nu există beneficiari valizi pentru acest emitent.
                    </div>
                  ) : (
                    <div className="checkbox-list">
                      {endUsers.map(user => (
                        <label key={user.id} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={selectedEndUserIds.includes(user.id)}
                            onChange={(e) => handleEndUserSelect(user.id, e.target.checked)}
                          />
                          {user.nume}
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="section-card">
            <div className="section-content">
              <div className="actions-container">
                <div className="actions-info">
                  <h3>Acțiuni</h3>
                  <p>Salvarea este posibilă doar dacă toate câmpurile sunt completate</p>
                </div>
                <div className="actions-buttons">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!allValid || saving}
                    className={`action-btn ${(!allValid || saving) ? 'action-btn-disabled' : 'action-btn-save'}`}
                  >
                    {saving
                      ? 'Se salvează...'
                      : isEditMode
                        ? 'Actualizează Articol și Legături'
                        : 'Salvează Articol și Legături'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, full = false, mono = false }) {
  return (
    <div className={full ? 'form-field party-address' : 'form-field'}>
      <div className="form-field-label">{label}</div>
      <div className={`form-field-value ${mono ? 'form-field-value-mono' : ''}`}>{String(value)}</div>
    </div>
  );
}

function Input({ label, value, onChange, mono = false, error, full = false, placeholder }) {
    const className = `form-field ${full ? 'party-address' : ''}`;
    return (
      <div className={className}>
        <div className={`form-field-label ${error ? 'form-field-label-unknown' : ''}`}>{label}</div>
        <input
          className={`clients-input ${mono ? 'form-field-value-mono' : ''} ${error ? 'form-field-input-error' : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        {error && <div className="form-field-error">{error}</div>}
      </div>
    );
}
